import {
  UseMutationResult,
  UseQueryResult,
  queryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { MAX_BATCH_UPLOAD_SIZE } from '../../constants/file-upload.constants';
import {
  IBatchConfirmUploadResponse,
  IRoundDocumentConfirmRequest,
  IRoundDocumentDeleteResponse,
  IRoundDocumentDownloadUrlResponse,
  IRoundDocumentMetadata,
  IRoundDocumentUploadUrlRequest,
  RoundDocumentType,
} from '../../data-models/round-document.data-model';
import {
  confirmBatchRoundDocumentUpload,
  confirmRoundDocumentUpload,
  deleteRoundDocument,
  deleteRoundDocumentById,
  getRoundDocumentDownloadUrl,
  getRoundDocuments,
  requestBatchRoundDocumentUploadUrls,
  requestRoundDocumentUploadUrl,
} from '../queries/MaggieRoundDocumentQueries';
import { queryClient } from '../MaggieQueryClient';

/** Query key factory for type safety and consistency */
export const roundDocumentKeys = {
  all: ['round-documents'] as const,
  list: (roundId?: number | null) => [...roundDocumentKeys.all, roundId] as const,
  download: (roundId?: number | null, documentType?: RoundDocumentType | null) =>
    [...roundDocumentKeys.all, 'download', roundId, documentType] as const,
};

/** Query options factory for a round's document list. Opts back into
 * window-focus refetching (the global default is off). */
export const roundDocumentsQueryOptions = (roundId?: number | null) =>
  queryOptions({
    queryKey: roundDocumentKeys.list(roundId),
    queryFn: () => getRoundDocuments(roundId!),
    enabled: Boolean(roundId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: false,
  });

/** Query options factory for a presigned download URL (never cached). */
export const roundDocumentDownloadUrlQueryOptions = (
  roundId?: number | null,
  documentType?: RoundDocumentType | null
) =>
  queryOptions({
    queryKey: roundDocumentKeys.download(roundId, documentType),
    queryFn: () => getRoundDocumentDownloadUrl(roundId!, documentType!),
    enabled: Boolean(roundId && documentType),
    staleTime: 0, // Don't cache download URLs
    refetchOnMount: false,
  });

/**
 * Hash a string for safe logging (non-cryptographic)
 * Used to protect PII in logs
 */
function hashForLogging(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `hash_${Math.abs(hash).toString(16)}`;
}

/**
 * Hook to fetch all documents for a specific round
 * @param roundId - The round ID
 * @returns Query result with array of document metadata
 */
export function useRoundDocuments(roundId?: number | null): UseQueryResult<IRoundDocumentMetadata[], Error> {
  return useQuery(roundDocumentsQueryOptions(roundId));
}

/**
 * Hook to get a presigned download URL for a document
 * @param roundId - The round ID
 * @param documentType - Type of document
 * @returns Query result with download URL
 */
export function useRoundDocumentDownloadUrl(
  roundId?: number | null,
  documentType?: RoundDocumentType | null
): UseQueryResult<IRoundDocumentDownloadUrlResponse, Error> {
  return useQuery(roundDocumentDownloadUrlQueryOptions(roundId, documentType));
}

interface UploadRoundDocumentParams {
  roundId: number;
  file: File;
  documentType: RoundDocumentType;
}

/**
 * Hook to upload a document for a round
 * Handles the complete 3-step upload flow:
 * 1. Request presigned URL
 * 2. Upload to S3
 * 3. Confirm upload
 */
export function useUploadRoundDocument(): UseMutationResult<
  IRoundDocumentMetadata,
  Error,
  UploadRoundDocumentParams
> {
  return useMutation({
    mutationFn: async ({ roundId, file, documentType }: UploadRoundDocumentParams) => {
      // Step 1: Request presigned URL
      const uploadUrlPayload: IRoundDocumentUploadUrlRequest = {
        documentType,
        fileName: file.name,
        contentType: file.type,
      };
      const { fileId, uploadUrl, s3Key } = await requestRoundDocumentUploadUrl(roundId, uploadUrlPayload);

      // Step 2: Upload file to S3
      try {
        const arrayBuffer = await file.arrayBuffer();
        await axios.put(uploadUrl, arrayBuffer, {
          headers: { 'Content-Type': file.type },
        });
      } catch (error) {
        // If S3 upload fails, throw an error to prevent confirm step
        console.error('S3 upload failed:', error);

        // Preserve stack trace using Error.cause
        if (error instanceof Error) {
          const enhancedError = new Error(`Failed to upload file to S3: ${error.message}`, {
            cause: error,
          });
          throw enhancedError;
        }

        throw error;
      }

      // Step 3: Confirm upload
      try {
        const confirmPayload: IRoundDocumentConfirmRequest = {
          documentType,
          fileId,
          fileName: file.name,
          s3Key,
          fileSize: file.size,
          contentType: file.type,
        };
        return await confirmRoundDocumentUpload(roundId, confirmPayload);
      } catch (error) {
        // If confirm fails, the file is already uploaded to S3
        // Log this for potential cleanup, but still throw the error
        console.error('Failed to confirm upload after S3 success - orphaned file:', { s3Key, fileId });

        // Log to Sentry for monitoring (hash file name for privacy)
        Sentry.captureException(error instanceof Error ? error : new Error('Failed to confirm upload'), {
          tags: {
            feature: 'round-documents-upload',
            severity: 'high',
          },
          contexts: {
            orphanedFile: {
              s3KeySuffix: s3Key.substring(s3Key.lastIndexOf('/') + 1),
              fileId,
              fileNameHash: hashForLogging(file.name),
            },
          },
          level: 'error',
        });

        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the documents list for this round
      queryClient.invalidateQueries({
        queryKey: roundDocumentKeys.list(variables.roundId),
      });
    },
  });
}

interface UploadMultipleRoundDocumentsParams {
  roundId: number;
  files: Array<{ file: File; documentType: RoundDocumentType }>;
}

/**
 * Hook to upload multiple documents for a round
 * Handles the complete batch upload flow:
 * 1. Request presigned URLs for all files
 * 2. Upload files to S3 in parallel
 * 3. Confirm uploads in batch
 * Returns partial success information (which files succeeded/failed)
 */
export function useUploadMultipleRoundDocuments(): UseMutationResult<
  IBatchConfirmUploadResponse,
  Error,
  UploadMultipleRoundDocumentsParams
> {
  return useMutation({
    mutationFn: async ({ roundId, files }: UploadMultipleRoundDocumentsParams) => {
      // Validate input to prevent DoS attacks and resource exhaustion
      if (files.length === 0) {
        throw new Error('No files selected for upload');
      }

      if (files.length > MAX_BATCH_UPLOAD_SIZE) {
        throw new Error(
          `Cannot upload more than ${MAX_BATCH_UPLOAD_SIZE} files at once. ` +
            `Please select ${MAX_BATCH_UPLOAD_SIZE} or fewer files.`
        );
      }

      // Step 1: Request presigned URLs for all files
      const uploadUrlPayloads = files.map((f) => ({
        documentType: f.documentType,
        fileName: f.file.name,
        contentType: f.file.type,
      }));

      const uploadUrls = await requestBatchRoundDocumentUploadUrls(roundId, { files: uploadUrlPayloads });

      // Step 2: Upload files to S3 with concurrency limit (max 3 at a time)
      const UPLOAD_CONCURRENCY_LIMIT = 3;

      // Pre-initialize results array to prevent sparse array issues
      type UploadResult =
        | { success: true; urlData: (typeof uploadUrls)[number]; file: (typeof files)[number] }
        | { success: false; error: string; file: (typeof files)[number] };
      const uploadResults: UploadResult[] = new Array(uploadUrls.length);
      const executing: Promise<void>[] = [];

      for (let index = 0; index < uploadUrls.length; index++) {
        const urlData = uploadUrls[index];
        const file = files[index].file;

        const uploadPromise = (async () => {
          try {
            const arrayBuffer = await file.arrayBuffer();
            await axios.put(urlData.uploadUrl, arrayBuffer, {
              headers: { 'Content-Type': file.type },
            });
            uploadResults[index] = {
              success: true as const,
              urlData,
              file: files[index],
            };
          } catch (error) {
            console.error(`S3 upload failed for ${file.name}:`, error);

            // Preserve error details for debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            uploadResults[index] = {
              success: false as const,
              error: errorMessage,
              file: files[index],
            };
          }
        })();

        // Wrap promise to handle cleanup after completion - use wrappedPromise reference to avoid race condition
        const wrappedPromise = uploadPromise.finally(() => {
          // Remove from executing array when done
          const idx = executing.indexOf(wrappedPromise);
          if (idx > -1) {
            executing.splice(idx, 1);
          }
        });

        executing.push(wrappedPromise);

        // Wait for one to finish if we've hit the concurrency limit
        if (executing.length >= UPLOAD_CONCURRENCY_LIMIT) {
          await Promise.race(executing);
        }
      }

      // Wait for all remaining uploads to complete
      await Promise.all(executing);

      // Filter successful uploads for confirmation
      const successfulUploads = uploadResults.filter(
        (r): r is Extract<(typeof uploadResults)[number], { success: true }> => r.success
      );
      const failedUploads = uploadResults.filter(
        (r): r is Extract<(typeof uploadResults)[number], { success: false }> => !r.success
      );

      // If all uploads failed, throw an error
      if (successfulUploads.length === 0) {
        throw new Error('All file uploads to S3 failed');
      }

      // Step 3: Confirm successful uploads
      const confirmPayloads = successfulUploads.map((result) => ({
        documentType: result.file.documentType,
        fileId: result.urlData.fileId,
        fileName: result.file.file.name,
        s3Key: result.urlData.s3Key,
        fileSize: result.file.file.size,
        contentType: result.file.file.type,
      }));

      try {
        const confirmResult = await confirmBatchRoundDocumentUpload(roundId, { uploads: confirmPayloads });

        // Add S3 upload failures to the errors array
        const allErrors = [
          ...confirmResult.errors,
          ...failedUploads.map((f) => ({
            fileName: f.file.file.name,
            error: f.error || 'Failed to upload to S3',
          })),
        ];

        return {
          success: confirmResult.success,
          errors: allErrors,
        };
      } catch (error) {
        // If confirm fails, some files are already uploaded to S3 (orphaned)
        // This is a critical error that needs immediate attention
        const orphanedS3Keys = successfulUploads.map((r) => r.urlData.s3Key);
        const errorMessage = 'Failed to confirm batch upload - ORPHANED FILES IN S3';

        console.error(errorMessage, {
          s3Keys: orphanedS3Keys,
          roundId,
          fileCount: successfulUploads.length,
        });

        // Log to Sentry for monitoring and alerting
        // IMPORTANT: Hash file names to prevent PII exposure
        Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
          tags: {
            feature: 'round-documents-upload',
            severity: 'critical',
          },
          contexts: {
            orphanedFiles: {
              // Only include S3 key suffixes (after last /)
              s3KeySuffixes: orphanedS3Keys.map((key) => key.substring(key.lastIndexOf('/') + 1)),
              roundId,
              fileCount: successfulUploads.length,
              // Hash file names to protect sensitive data
              fileNameHashes: successfulUploads.map((r) => hashForLogging(r.file.file.name)),
            },
          },
          level: 'error',
        });

        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the documents list for this round
      queryClient.invalidateQueries({
        queryKey: roundDocumentKeys.list(variables.roundId),
      });
    },
  });
}

interface DeleteRoundDocumentParams {
  roundId: number;
  documentType: RoundDocumentType;
}

/**
 * Hook to delete a document from a round
 */
export function useDeleteRoundDocument(): UseMutationResult<
  IRoundDocumentDeleteResponse,
  Error,
  DeleteRoundDocumentParams
> {
  return useMutation({
    mutationFn: ({ roundId, documentType }: DeleteRoundDocumentParams) =>
      deleteRoundDocument(roundId, documentType),
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the documents list for this round
      queryClient.invalidateQueries({
        queryKey: roundDocumentKeys.list(variables.roundId),
      });
    },
  });
}

interface DeleteRoundDocumentByIdParams {
  roundId: number;
  documentId: number;
}

/**
 * Hook to delete a specific document from a round by document ID
 */
export function useDeleteRoundDocumentById(): UseMutationResult<
  IRoundDocumentDeleteResponse,
  Error,
  DeleteRoundDocumentByIdParams
> {
  return useMutation({
    mutationFn: ({ roundId, documentId }: DeleteRoundDocumentByIdParams) =>
      deleteRoundDocumentById(roundId, documentId),
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the documents list for this round
      queryClient.invalidateQueries({
        queryKey: roundDocumentKeys.list(variables.roundId),
      });
    },
  });
}
